/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState } from 'react'
import 'semantic-ui-css/semantic.min.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { useLocales } from 'hooks/useLocales'
import { ParamRouter, Route } from 'components/paramRouter'
import { PastVotesList } from 'components/pastVotesList'
import { useStore } from 'hooks/useStore'
import { useGetCanton } from 'hooks/useGetCanton'
import { MunicipalitySearch } from '@ta-interaktiv/react-municipality-search'
import { Article } from 'components/article'
import { DateDropdown } from 'components/dateDropdown'
import { CountState } from 'components/countState'
import standaloneConfig from '../../../.taStandalonerc.yml'

const { locale, messages } = useLocales()
const ARTICLE_ID = standaloneConfig.articleId[locale]

export function AppComponent() {
  // Zustand is used to store the url parameters
  const { setUrlParams, urlParams } = useStore()
  const [config, setConfig] = useState([])
  const [filteredConfig, setFilteredConfig] = useState([])
  const canton = useGetCanton()

  // Find the proper config according to the date in the url parameters
  // if no date is found, use the latest config
  const selectedConfig =
    config.find(item => item.date === urlParams.date) || config[0]

  let municipalityStateLang = selectedConfig?.municipality_state

  if (selectedConfig) {
    if (new Date(selectedConfig.date) > new Date('2022-11-27')) {
      municipalityStateLang = municipalityStateLang + '_' + locale
    }
    console.log(municipalityStateLang)
  }

  // If the user chooses a new municipality, update the url parameters accordingly
  const handleMunicipalitySelect = municipality => {
    setUrlParams({
      bfs: municipality.GDENR,
      date: selectedConfig.date
    })
  }

  /**
   * Fetch the config from the server
   */
  const fetchConfig = () => {
    const configUrl =
      'https://atm-vote.s3.eu-west-1.amazonaws.com/frontend_config/current_config.json'

    return window
      .fetch(configUrl, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      })
      .then(response => {
        if (response.ok) {
          return response.json()
        }
        if (response.status === 404) {
          return new Promise(resolve => {
            resolve({ content: null })
          })
        }
        throw new Error(response.status + ': ' + response.statusText)
      })
      .catch(error => {
        if (error.message.startsWith('Network')) {
          error.message = '500: ' + error.message
        }
        // this is a catch all, if municipality content cannot be loaded for any
        // internal server reason, it is handled like a 404
        return new Promise(resolve => {
          resolve({ content: null })
        })
      })
  }

  // Fetch the config from the server when the component mounts
  useEffect(() => {
    fetchConfig().then(data => {
      data.config.map(item => (item.formatedDate = new Date(item.date)))
      setConfig(data.config)
    })
  }, [])

  /**
   * When the url params changes, we need to update the filtered config
   */
  useEffect(() => {
    if (urlParams.bfs) {
      const BASE_URL =
        process.env.NODE_ENV === 'production'
          ? 'https://atm.tamedia.ch:443'
          : 'http://localhost:5000'
      fetch(
        `${BASE_URL}/api/vote/get_all_available_votes?bfsid=${urlParams.bfs}`
      )
        .then(response => {
          if (response.ok) {
            return response.json()
          }
          if (response.status === 404) {
            return new Promise(resolve => {
              resolve({ content: null })
            })
          }
          throw new Error(response.status + ': ' + response.statusText)
        })
        .then(availableVotesData => {
          const availableVotes = config.filter(item =>
            availableVotesData.content.available_votes.includes(item.date)
          )
          setFilteredConfig(availableVotes)
        })
        .catch(error => {
          if (error.message.startsWith('Network')) {
            error.message = '500: ' + error.message
          }
          // this is a catch all, if municipality content cannot be loaded for any
          // internal server reason, it is handled like a 404
          return new Promise(resolve => {
            resolve({ content: null })
          })
        })
    } else {
      setFilteredConfig(config)
    }
  }, [urlParams.bfs, config])

  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <ParamRouter>
        <Route params={['bfs']} strict={false}>
          {selectedConfig && (
            <>
              <Article
                bfs={urlParams.bfs}
                date={selectedConfig.date}
                lang={locale}
                format={selectedConfig.format}
              />
              <div
                id='search-box'
                className='ui blue vertical segment'
                style={{ zIndex: 99 }}
              >
                <div className='ui container'>
                  <h4 className='ui header'>
                    {messages.municipalitySearchBoxTitle}
                    <div className='sub header'>
                      {messages.votationDateTitle}{' '}
                      {selectedConfig.formatedDate.toLocaleDateString(
                        `${locale}-CH`,
                        {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }
                      )}
                    </div>
                  </h4>
                  <div style={{ zIndex: 1, position: 'relative' }}>
                    <MunicipalitySearch
                      locale={locale}
                      dedupe
                      onSelectionHandler={handleMunicipalitySelect}
                      municipalityData={municipalityStateLang}
                      resultBackgroundColor='#ffffff'
                    />
                  </div>
                  <div
                    className='ui primary button'
                    onClick={() => {
                      // Do the people actually use this menu?
                      window.dataLayer = window.dataLayer || []
                      window.dataLayer.push({
                        event: 'Interactions',
                        event_action: 'click',
                        event_label: `nav:homepage`
                      })
                      setUrlParams({
                        date: null,
                        bfs: null
                      })
                    }}
                  >
                    <i className='ui chevron left icon' /> {messages.backToMain}
                  </div>
                </div>
              </div>

              <div className='ui vertical segment' id='latestVotesList'>
                <div className='ui container'>
                  <PastVotesList votes={filteredConfig} format='main' />
                </div>
              </div>
            </>
          )}
        </Route>
        <Route params={['date']} defaultRoute>
          {selectedConfig && (
            <>
              <header className='ui vertical very fitted  segment'>
                <div className='ui container'>
                  <div className='ui header'>
                    <h1>{messages.title}</h1>
                    <h2>{messages.lead}</h2>
                  </div>
                </div>
              </header>

              <div
                id='search-box'
                className='ui vertical segment vote-title'
                style={{
                  paddingBottom: '0.75em',
                  marginTop: '0.85em',
                  paddingTop: '1.5em',
                  borderTop: '1px solid #20234612'
                }}
              >
                <div className='ui container'>
                  <div
                    className='sub header'
                    style={{
                      lineHeight: '1.32em',
                      zIndex: 100,
                      position: 'relative'
                    }}
                  >
                    {messages.votationDateTitle}{' '}
                    <DateDropdown
                      value={selectedConfig.date}
                      onChange={(e, { value }) => {
                        // Do the people actually use this menu?
                        window.dataLayer = window.dataLayer || []
                        window.dataLayer.push({
                          event: 'Interactions',
                          event_action: 'click',
                          event_label: `nav:${value}`
                        })
                        setUrlParams({
                          date: value
                        })
                      }}
                      options={filteredConfig.map(item => ({
                        key: item.date,
                        value: item.date,
                        text: item.formatedDate.toLocaleDateString(
                          `${locale}-CH`,
                          {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }
                        )
                      }))}
                    />
                  </div>
                  {/* <div>
                    <small>{messages.learnMoreAboutVotes}</small>
                  </div> */}
                </div>
              </div>

              <div className='ui blue vertical segment' style={{ zIndex: 99 }}>
                <div className='ui container'>
                  <h4 className='ui header'>{messages.findMunicipality}</h4>
                  <MunicipalitySearch
                    locale={locale}
                    dedupe
                    showLastSelectedMunicipalities
                    numberOfLastSelectedMunicipalities={1}
                    onSelectionHandler={handleMunicipalitySelect}
                    municipalityData={municipalityStateLang}
                    resultBackgroundColor='#ffffff'
                  />
                  {/* if votes_* is empty, there are no national votes, and count state should not be shown */}
                  {selectedConfig.votes_de !== '' &&
                    selectedConfig.votes_fr !== '' && (
                      <CountState
                        locale={locale}
                        date={selectedConfig.date.replaceAll('-', '')}
                        canton={canton}
                      />
                    )}
                </div>
              </div>

              <div className='ui vertical segment' id='latestVotesList'>
                <div className='ui container'>
                  <PastVotesList votes={filteredConfig} format='municipality' />
                </div>
              </div>
            </>
          )}
        </Route>
      </ParamRouter>

      <div className='ui vertical grey segment' id='methodik'>
        <div className='ui text container'>
          <h4>{messages.methodologyTitle}</h4>
          <div className='ta text'>{messages.methodologyText}</div>
        </div>
      </div>

      <Footer
        title={messages.feedbackTitle}
        questions={messages.feedbackQuestions}
        mailTo='atm@tamedia.ch'
        hidePortfolioTeaser
        articleId={ARTICLE_ID}
        credits={messages.credits}
      />
    </div>
  )
}
